import { useEffect, useRef, useState } from "react";
import { LuFilter, LuUsers } from "react-icons/lu";
import { MdOutlineDelete } from "react-icons/md";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../../components/Button/Button";
import ConsultaFormContainer from "../../../components/ConsultaFormContainer/ConsultaFormContainer";
import Container from "../../../components/Container/Container";
import FilterApplied from "../../../components/FilterApplied/FilterApplied";
import Input from "../../../components/Input/Input";
import { LicenseUnitSelector } from "../../../components/LicenseUnitSelector/LicenseUnitSelector";
import ModalComponent from "../../../components/Modal/ModalComponent";
import SearchContainer from "../../../components/SearchContainer/SearchContainer";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Select from "../../../components/Select/Select";
import SyncButton from "../../../components/SyncButton/SyncButton";
import { useListManager } from "../../../contexts/list.manager";
import { useUserStore } from "../../../contexts/userStore";
import { useTranslation } from "react-i18next";
import FormAccessGroups from "../FormAccessGroups/FormAccessGroups";
import ListAccessGroups from "../ListAccessGroups/ListAccessGroups";
import "./ConsultaAccessGroups.css";

const EMPTY_FILTERS = {
	search: "",
	unitId: "",
	startDate: "",
	startTime: "",
	endDate: "",
	endTime: "",
	direction: "",
};

const FILTER_KEYS = Object.keys(EMPTY_FILTERS);
const DIRECTIONS = [
	{ value: "1", text: "Entrada e Saída" },
	{ value: "2", text: "Somente Entrada" },
	{ value: "3", text: "Somente Saída" },
	{ value: "4", text: "Entrada e Urna" },
];
const FILTER_LABELS = {
	search: "Busca",
	unitId: "Licença",
	startDate: "Vigência a partir de",
	startTime: "Horário inicial",
	endDate: "Vigência até",
	endTime: "Horário final",
	direction: "Sentido",
};

function readFiltersFromUrl(searchParams) {
	return FILTER_KEYS.reduce((filters, key) => {
		filters[key] = searchParams.get(key) ?? "";
		return filters;
	}, {});
}

const ConsultaAccessGroups = () => {
	const { lineSelected } = useListManager();
	const { selectedCondominio, condominiums } = useUserStore();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
	const [draftFilters, setDraftFilters] = useState(() => readFiltersFromUrl(searchParams));
	const filtersRef = useRef(filters);
	filtersRef.current = filters;
	const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const [refreshKey, setRefreshKey] = useState(0);
	const serializedSearchParams = searchParams.toString();
	const { t } = useTranslation();

	useEffect(() => {
		const restoredFilters = readFiltersFromUrl(
			new URLSearchParams(serializedSearchParams),
		);
		setFilters(restoredFilters);
		setDraftFilters(restoredFilters);
		setSearch(restoredFilters.search);
	}, [serializedSearchParams]);

	useEffect(() => {
		if (!selectedCondominio) return;
		const selectedUnitId = String(selectedCondominio);
		if (filtersRef.current.unitId === selectedUnitId) return;

		const nextFilters = { ...filtersRef.current, unitId: selectedUnitId };
		setFilters(nextFilters);
		setDraftFilters(nextFilters);
		const nextSearchParams = new URLSearchParams();
		FILTER_KEYS.forEach((key) => {
			if (nextFilters[key]) nextSearchParams.set(key, nextFilters[key]);
		});
		setSearchParams(nextSearchParams, { replace: true });
	}, [selectedCondominio, setSearchParams]);

	useEffect(() => {
		if (location.state?.deletedId || location.state?.refresh) {
			setRefreshKey((key) => !key);
			navigate(location.pathname, { replace: true, state: {} });
		}
	}, [location.state, navigate, location.pathname]);

	const syncFiltersToUrl = (nextFilters) => {
		const nextSearchParams = new URLSearchParams();
		FILTER_KEYS.forEach((key) => {
			if (nextFilters[key]) nextSearchParams.set(key, nextFilters[key]);
		});
		setSearchParams(nextSearchParams, { replace: true });
	};

	const applyFilters = (nextFilters) => {
		setFilters(nextFilters);
		setDraftFilters(nextFilters);
		setSearch(nextFilters.search);
		syncFiltersToUrl(nextFilters);
	};

	const clearFilters = () => {
		applyFilters({ ...EMPTY_FILTERS });
		setModalIsOpen(false);
	};

	const removeFilter = (key) => {
		applyFilters({ ...filters, [key]: "" });
	};

	const getFilterLabel = (key, value) => {
		if (key === "unitId") {
			return condominiums.find((unit) => String(unit.id) === String(value))?.name || value;
		}
		if (key === "direction") {
			return DIRECTIONS.find((direction) => direction.value === String(value))?.text || value;
		}
		return value;
	};

	return (
		<div className="consulta-container">
			<div className="search-list-wrapper">
				<SearchContainer
					icon={<LuUsers size={20} />}
					title="Grupos de Acesso"
					navigate="../cadastro-access-groups"
					buttonLabel="Adicionar Grupo"
					className="mb-1!"
					aditionalButton1={
						<>
							<Button
								className="button-white"
								label="Filtros"
								icon={<LuFilter size={18} />}
								onClick={() => {
									setDraftFilters(filters);
									setModalIsOpen(true);
								}}
							/>
							<Button
								className="button-white"
								label="Limpar filtros"
								icon={<MdOutlineDelete size={17} />}
								onClick={clearFilters}
							/>
						</>
					}
					aditionalButton2={
						<SyncButton
							type={"group"}
							selectedCondominio={selectedCondominio}
						/>
					}
				>
					<SearchInput
						value={search}
						placeholder="Buscar Grupo"
						onChange={({ target }) => setSearch(target.value)}
						onKeyPress={(event) => {
							if (event.key === "Enter") {
								applyFilters({ ...filters, search });
							}
						}}
					/>
				</SearchContainer>
				<Container className="pt-0!">
					{FILTER_KEYS.filter((key) => filters[key]).length > 0 && (
						<div className="flex flex-wrap gap-2 mt-0!">
							{FILTER_KEYS.filter((key) => filters[key]).map((key) => (
								<FilterApplied
									key={key}
									filter={`${FILTER_LABELS[key]}: ${getFilterLabel(key, filters[key])}`}
									removeFilter={() => removeFilter(key)}
								/>
							))}
						</div>
					)}
				</Container>
				<Container className="mt-3!">
					<ListAccessGroups filters={filters} refreshKey={refreshKey} />
				</Container>
			</div>
			<ConsultaFormContainer>
				<FormAccessGroups accessGroup={lineSelected} type="consulta" />
			</ConsultaFormContainer>
			<ModalComponent
				modalIsOpen={modalIsOpen}
				modalText="Filtros de grupos de acesso"
				handleConfirm={() => {
					applyFilters({ ...draftFilters, search: filters.search });
					setModalIsOpen(false);
				}}
				handleCancel={() => setModalIsOpen(false)}
			>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[min(80vw,700px)]">
					<LicenseUnitSelector
						label="Licença"
						unitId={draftFilters.unitId}
						setUnitId={(unitId) =>
							setDraftFilters((current) => ({
								...current,
								unitId: unitId ? String(unitId) : "",
							}))
						}
					/>
					<Select
						labelText="Sentido Permitido"
						value={draftFilters.direction}
						options={DIRECTIONS}
						onChange={({ target }) =>
							setDraftFilters((current) => ({
								...current,
								direction: target.value,
							}))
						}
					/>
					<div className="filters-group group-date-range mb-3.5! md:col-span-2">
						<h3 className="filters-group-title">{t("common.timeInterval")}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full!">
							<Input
								type="date"
								labelText={t("common.startDate")}
								value={draftFilters.startDate}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										startDate: target.value,
										startTime: target.value ? current.startTime : "",
									}))
								}
							/>
							<Input
								type="time"
								min="00:00"
								max="23:59"
								labelText={t("common.startTime")}
								value={draftFilters.startTime}
								disabled={!draftFilters.startDate}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										startTime: target.value,
									}))
								}
							/>
							<Input
								type="date"
								labelText={t("common.endDate")}
								value={draftFilters.endDate}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										endDate: target.value,
										endTime: target.value ? current.endTime : "",
									}))
								}
							/>
							<Input
								type="time"
								min="00:00"
								max="23:59"
								labelText={t("common.endTime")}
								value={draftFilters.endTime}
								disabled={!draftFilters.endDate}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										endTime: target.value,
									}))
								}
							/>
						</div>
					</div>
				</div>
			</ModalComponent>
		</div>
	);
};

export default ConsultaAccessGroups;
